/**
 * Sidebar panel for the Notes and Lookout (alerts) tabs. Polymorphic on
 * `noteType`: renders `<NotesView>` for user notes and
 * `<AlertsSidebar>` for alert notes. Extracted from MPV's `renderNotes`
 * helper, which was called twice — once per tab — with the same closure
 * captures but a different noteType discriminator.
 */

import * as I from 'immutable';
import React from 'react';

import {AlertPolicyContextProvider} from 'app/components/Alerts/AlertPolicyProvider';
import AlertsSidebar from 'app/components/Alerts/AlertsSidebar';
import NotesView from 'app/components/NotesReports/NotesView';
import {ReportExportState} from 'app/components/NotesReports/ReportsView';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {PolygonFeature} from 'app/providers/MapPolygonStateProvider';
import {NotesActions, NotesState, StateApiNote} from 'app/stores/NotesStore';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import * as noteUtils from 'app/utils/noteUtils';

import {MapStateDispatch} from '../MapStateProvider';
import cs from '../styles.module.scss';

interface Props {
  noteType: StateApiNote['noteType'];
  feature: I.ImmutableOf<ApiFeature>;
  features: I.ImmutableOf<ApiFeature[]>;
  notesState: NotesState;
  notesActions: NotesActions;
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;

  // User-context props
  firebaseToken: string;
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  organizationUsers: ApiOrganizationUser[];

  // Map-state props
  imageRefs: mapUtils.MapImageRefs;
  latestCameraOptions: NotesViewCameraOptions | null;
  isOverlayVisibleByName: Record<string, boolean>;
  setIsOverlayVisibleByName: (visibility: Record<string, boolean>) => void;
  zoomToGeometry: (geometry: GeoJSON.GeoJSON | null) => void;
  mapStateDispatch: MapStateDispatch;
  openAnalyzePolygonPopup: (polygonFeature: PolygonFeature) => void;

  // Notes-filter / report-export props
  notesFilterState: noteUtils.NotesFilterState;
  setNotesFilterState: (state?: noteUtils.NotesFilterState) => void;
  setReportExportModalState: (state: ReportExportState | null) => void;

  // Action callbacks
  updateFeature: FeaturesActions['updateFeature'];
  setTags: LoggedInUserActions['setOrganizationTags'];
  promptForCoordinates: () => void;
}

type NotesViewCameraOptions = NonNullable<React.ComponentProps<typeof NotesView>['cameraOptions']>;

export default function NotesPanel(props: Props) {
  const {
    noteType,
    feature,
    features,
    notesState,
    notesActions,
    selectedFeatureCollection,
    firebaseToken,
    organization,
    profile,
    organizationUsers,
    imageRefs,
    latestCameraOptions,
    isOverlayVisibleByName,
    setIsOverlayVisibleByName,
    zoomToGeometry,
    mapStateDispatch,
    openAnalyzePolygonPopup,
    notesFilterState,
    setNotesFilterState,
    setReportExportModalState,
    updateFeature,
    setTags,
    promptForCoordinates,
  } = props;

  const enrolledLayerKeys =
    layerUtils.getEnrolledLayerKeysForFeatureCollection(selectedFeatureCollection);

  if (noteType === 'user') {
    return (
      <NotesView
        firebaseToken={firebaseToken}
        className={cs.notes}
        organization={organization}
        profile={profile}
        cameraOptions={latestCameraOptions}
        zoomToGeometry={zoomToGeometry}
        onSelectNote={(noteId) => notesActions.focusNote(noteId)}
        onSelectImageRefs={(imageRefs) => mapStateDispatch({type: 'SET_IMAGE_REFS', imageRefs})}
        onSelectOverlays={(visibility) => setIsOverlayVisibleByName(visibility)}
        selectedFeatureCollection={selectedFeatureCollection}
        imageRefs={imageRefs}
        selectedFeature={feature}
        enrolledLayerKeys={enrolledLayerKeys}
        openAnalyzePolygonPopup={openAnalyzePolygonPopup}
        promptForCoordinates={promptForCoordinates}
        setTags={setTags}
        organizationUsers={organizationUsers}
        notesFilterState={notesFilterState}
        setNotesFilterState={setNotesFilterState}
        setReportExportModalState={setReportExportModalState}
        isOverlayVisibleByName={isOverlayVisibleByName}
        notesState={notesState}
        notesActions={notesActions}
      />
    );
  }

  if (noteType === 'alert') {
    return (
      <AlertPolicyContextProvider featureCollection={selectedFeatureCollection}>
        <AlertsSidebar
          feature={feature}
          allFeatures={features}
          featureCollection={selectedFeatureCollection}
          enrolledLayerKeys={enrolledLayerKeys}
          updateFeature={updateFeature}
          zoomToGeometry={zoomToGeometry}
          onSelectNote={(noteId) => notesActions.focusNote(noteId)}
          onSelectImageRefs={(imageRefs) => mapStateDispatch({type: 'SET_IMAGE_REFS', imageRefs})}
          notesState={notesState}
          notesActions={notesActions}
        />
      </AlertPolicyContextProvider>
    );
  }

  return null;
}
